/**
 * Chat Input Feature Types
 *
 * This file contains TypeScript types and interfaces for the modernized chat input features.
 * These are designed to be easily integrated with backend APIs when ready.
 */

/**
 * Thinking modes for AI responses
 * These modes control how the AI processes and responds to user queries
 */
export type ThinkingMode = 'balanced' | 'creative' | 'precise' | 'fast';

/**
 * Thinking mode configuration with display properties
 */
export interface ThinkingModeConfig {
  value: ThinkingMode;
  label: string;
  description: string;
  icon: string; // Lucide icon name
}

/**
 * Configuration for all available thinking modes
 */
export const THINKING_MODES: ThinkingModeConfig[] = [
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Balanced approach between speed and quality',
    icon: 'scale',
  },
  {
    value: 'creative',
    label: 'Creative',
    description: 'More creative and exploratory responses',
    icon: 'sparkles',
  },
  {
    value: 'precise',
    label: 'Precise',
    description: 'Focused, accurate, and detailed responses',
    icon: 'target',
  },
  {
    value: 'fast',
    label: 'Fast',
    description: 'Quick responses for simple queries',
    icon: 'zap',
  },
];

/**
 * Web search toggle state
 * When enabled, the AI can search the web for current information
 */
export type WebSearchState = boolean;

/**
 * File attachment types supported by the chat input
 * Extend this type when adding more attachment support
 */
export type AttachmentType = 'file' | 'image' | 'document' | 'code';

/**
 * File attachment metadata
 * This will be sent to the backend when file upload API is ready
 */
export interface FileAttachment {
  id: string;
  file: File;
  type: AttachmentType;
  name: string;
  size: number;
  preview?: string; // For images - base64 preview or blob URL
  status: 'pending' | 'uploading' | 'uploaded' | 'error';
  error?: string;
}

/**
 * Chat input state containing all user-configurable options
 * This will be sent with each message to the backend
 */
export interface ChatInputState {
  message: string;
  thinkingMode: ThinkingMode;
  webSearchEnabled: WebSearchState;
  attachments: FileAttachment[];
}

/**
 * Message payload sent to the backend API
 * Extend this when backend APIs are ready
 */
export interface ChatMessagePayload extends ChatInputState {
  sessionId: string;
  skill: string;
  timestamp: number;
}

/**
 * Backend API response for file uploads
 * Placeholder for when file upload API is implemented
 */
export interface FileUploadResponse {
  fileId: string;
  url: string;
  name: string;
  size: number;
  mimeType: string;
}

/**
 * Props for chat input components
 */
export interface ChatInputProps {
  /** Current input value */
  value: string;
  /** Callback when input value changes */
  onChange: (value: string) => void;
  /** Callback when message is sent */
  onSend: (state: ChatInputState) => void | Promise<void>;
  /** Whether a message is currently being sent */
  disabled?: boolean;
  /** Placeholder text to display */
  placeholder?: string;
  /** Currently selected skill */
  skill?: string;
  /** Available skills to display */
  skills?: readonly { command: string; label: string }[];
  /** Callback when skill changes */
  onSkillChange?: (skill: string) => void;
}

/**
 * Props for attachment button component
 */
export interface AttachmentButtonProps {
  /** Current attachments */
  attachments: FileAttachment[];
  /** Callback when attachments change */
  onAttachmentsChange: (attachments: FileAttachment[]) => void;
  /** Whether attachments are disabled (during streaming, etc.) */
  disabled?: boolean;
}

/**
 * Props for thinking mode selector component
 */
export interface ThinkingModeSelectorProps {
  /** Currently selected thinking mode */
  value: ThinkingMode;
  /** Callback when thinking mode changes */
  onChange: (mode: ThinkingMode) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

/**
 * Props for web search toggle component
 */
export interface WebSearchToggleProps {
  /** Whether web search is enabled */
  enabled: boolean;
  /** Callback when toggle state changes */
  onChange: (enabled: boolean) => void;
  /** Whether the toggle is disabled */
  disabled?: boolean;
}
